import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { formatINR, optImg } from "@/lib/api";
import { PaisleyDivider, Lotus } from "@/lib/motifs";

const Cart = () => {
    const { cart, cartSubtotal, updateQty, removeFromCart } = useShop();
    const nav = useNavigate();
    const shipping = 0;
    const total = cartSubtotal + shipping;

    return (
        <div className="container-x py-16" data-testid="cart-page">
            <div className="text-center mb-10">
                <Lotus className="mx-auto mb-3" />
                <p className="font-devanagari text-[color:var(--gold)] text-sm">आपकी टोकरी</p>
                <h1 className="font-heading text-5xl text-[color:var(--maroon)] mt-1">Your Cart</h1>
                <PaisleyDivider className="mt-4" />
            </div>

            {cart.length === 0 ? (
                <div className="text-center py-20 border border-[color:var(--border-gold)] max-w-xl mx-auto">
                    <ShoppingBag size={40} className="mx-auto text-[color:var(--maroon)]" strokeWidth={1.2} />
                    <p className="font-heading text-2xl text-[color:var(--maroon)] mt-4">Your cart awaits its treasures</p>
                    <p className="font-body text-[color:var(--ink-muted)] mt-2 max-w-sm mx-auto">Discover our heritage collection and begin your Kanganya journey.</p>
                    <Link to="/shop" className="btn-primary mt-6 inline-flex" data-testid="cart-empty-shop">Browse Collection</Link>
                </div>
            ) : (
                <div className="grid lg:grid-cols-[1fr_400px] gap-12">
                    <div className="space-y-6" data-testid="cart-items">
                        {cart.map((item) => (
                            <div key={`${item.product_id}-${item.size}`} className="flex gap-4 md:gap-6 py-6 border-b border-[color:var(--border-gold)]" data-testid={`cart-item-${item.product_id}`}>
                                <div className="w-24 md:w-32 shrink-0 overflow-hidden border border-[color:var(--border-gold)] bg-white" style={{ borderTopLeftRadius: '999px', borderTopRightRadius: '999px' }}>
                                    <img src={optImg(item.image, 300)} alt={item.name} className="w-full aspect-[3/4] object-cover" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between gap-4">
                                        <div>
                                            <Link to={`/product/${item.product_id}`} className="font-heading text-xl text-[color:var(--maroon)] hover:text-[color:var(--copper)] transition-colors duration-300">
                                                {item.name}
                                            </Link>
                                            {item.size && <p className="font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--ink-muted)] mt-1">Size: {item.size}"</p>}
                                        </div>
                                        <button onClick={() => removeFromCart(item.product_id, item.size)} className="text-[color:var(--maroon)] hover:text-[color:var(--copper)] transition-colors duration-300 h-6" data-testid={`remove-item-${item.product_id}`}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                    <div className="flex justify-between items-end mt-4">
                                        <div className="flex items-center border border-[color:var(--border-gold)]">
                                            <button onClick={() => updateQty(item.product_id, item.size, item.quantity - 1)} className="p-2 hover:bg-[color:var(--ivory-2)] transition-colors duration-300" aria-label="Decrease" data-testid={`qty-dec-${item.product_id}`}><Minus size={12} /></button>
                                            <span className="px-4 font-body">{item.quantity}</span>
                                            <button onClick={() => updateQty(item.product_id, item.size, item.quantity + 1)} className="p-2 hover:bg-[color:var(--ivory-2)] transition-colors duration-300" aria-label="Increase" data-testid={`qty-inc-${item.product_id}`}><Plus size={12} /></button>
                                        </div>
                                        <span className="font-heading text-lg text-[color:var(--gold)] font-semibold">{formatINR(item.price * item.quantity)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <aside className="lg:sticky lg:top-32 self-start bg-white border border-[color:var(--border-gold)] p-8" data-testid="cart-summary">
                        <p className="font-ui text-xs tracking-widest-x uppercase text-[color:var(--copper)]">Order Summary</p>
                        <h3 className="font-heading text-2xl text-[color:var(--maroon)] mt-1">Ready for the ceremony?</h3>
                        <div className="mt-6 space-y-3 font-body">
                            <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Subtotal</span><span data-testid="cart-subtotal">{formatINR(cartSubtotal)}</span></div>
                            <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Shipping</span><span>{shipping === 0 ? <span className="text-[color:var(--emerald)]">Free</span> : formatINR(shipping)}</span></div>

                        </div>
                        <div className="border-t border-[color:var(--border-gold)] mt-5 pt-5 flex justify-between items-baseline">
                            <span className="font-heading text-xl text-[color:var(--maroon)]">Total</span>
                            <span className="font-heading text-2xl text-[color:var(--gold)] font-semibold" data-testid="cart-total">{formatINR(total)}</span>
                        </div>
                        <button onClick={() => nav("/checkout")} className="btn-primary w-full mt-6 justify-center" data-testid="checkout-button">
                            Proceed to Checkout
                        </button>
                        <p className="text-xs font-body text-[color:var(--ink-muted)] mt-3 text-center italic">
                            Free shipping on all orders
                        </p>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default Cart;
